import React from 'react';
import { Route, Switch } from 'react-router-dom';

import { FocusedCard } from '@brokerbay/design-components';
import { useParams, useHistory } from 'react-router';

import { Steps } from '../../types';
import {
  ClaimPrefix,
  OrgOnboardingStep,
  UserOnboardingStep,
} from '../../constants';
import { buildNavigationHandler, NavigationProps } from '../../util';
import { OrgOnboardingContextProvider } from '../../contexts/OrgOnboardingContext';
import UserProviderWrapper from './UserProviderWrapper';
import OnboardingOrgUserClaimView from './OnboardingOrgUserClaimView';
import PrimaryRoleView from './epic-2/PrimaryRoleView';
import FindMemberView from './epic-2/FindMemberView';
import CompleteProfileView from './epic-2/CompleteProfileView';
import RequestApprovalView from './epic-3/RequestApprovalView';
import SetPasswordView from './epic-3/SetPasswordView';
import ReviewTOSView from './epic-3/ReviewTOSView';
import VerifyUnlicensedView from './epic-2/VerifyUnlicensedView';

export const OnboardingOrgClaimView = () => {
  const { hash } = useParams<{ hash: string }>();

  const history = useHistory();
  const pathPrefix = ClaimPrefix.ORG_PROFILE;
  const defaultUrlParams = [{ name: 'hash', value: hash }];

  const navigationHandler = (
    nextStep: Steps,
    opts?: Partial<NavigationProps>,
  ) =>
    buildNavigationHandler({
      history,
      prefix: ClaimPrefix.ORG_PROFILE,
      urlParams: defaultUrlParams,
      nextStep,
      ...opts,
    });

  return (
    <OrgOnboardingContextProvider hash={hash}>
      <UserProviderWrapper>
        <FocusedCard>
          <Switch>
            <Route
              exact
              path={`${pathPrefix}/${OrgOnboardingStep.VERIFY_UNLICENSED}`}
              render={props => (
                <VerifyUnlicensedView
                  handleGoBackStep={navigationHandler(
                    UserOnboardingStep.PROFILE,
                  )}
                  {...props}
                />
              )}
            />

            <Route
              exact
              path={`${pathPrefix}/${OrgOnboardingStep.REQUEST_APPROVAL}`}
              render={props => (
                <RequestApprovalView
                  goBackStep={(
                    isLicensedUnclaimed: boolean,
                    claimHash: string,
                  ) =>
                    isLicensedUnclaimed
                      ? navigationHandler(UserOnboardingStep.PROFILE, {
                          prefix: ClaimPrefix.ORG_USER_PROFILE,
                          urlParams: [
                            ...defaultUrlParams,
                            { name: 'userHash', value: claimHash },
                          ],
                        })
                      : null
                  }
                  handleGoNextStep={(nextStep: UserOnboardingStep) =>
                    navigationHandler(nextStep)()
                  }
                />
              )}
            />

            <Route
              exact
              path={`${pathPrefix}/${OrgOnboardingStep.PRIMARY_ROLE}`}
              render={props => (
                <PrimaryRoleView
                  handleGoNextStep={(nextStep: OrgOnboardingStep) =>
                    navigationHandler(nextStep)()
                  }
                  {...props}
                />
              )}
            />

            <Route
              exact
              path={`${pathPrefix}/${OrgOnboardingStep.FIND_MEMBER}`}
              render={props => (
                <FindMemberView
                  handleGoBackStep={navigationHandler(
                    OrgOnboardingStep.PRIMARY_ROLE,
                  )}
                  {...props}
                />
              )}
            />

            <Route
              path={ClaimPrefix.ORG_USER_PROFILE}
              component={OnboardingOrgUserClaimView}
            />

            <Route
              exact
              path={`${pathPrefix}/${UserOnboardingStep.PROFILE}`}
              render={props => (
                <CompleteProfileView
                  isLicensed={false}
                  handleGoNextStep={navigationHandler(
                    OrgOnboardingStep.VERIFY_UNLICENSED,
                  )}
                  onRequestStepBack={navigationHandler(
                    OrgOnboardingStep.PRIMARY_ROLE,
                  )}
                  {...props}
                />
              )}
            />

            <Route
              exact
              path={`${pathPrefix}/${UserOnboardingStep.PASSWORD}`}
              render={props => (
                <SetPasswordView
                  goBackStep={(
                    isLicensedApproved: boolean,
                    claimHash: string,
                  ) =>
                    isLicensedApproved
                      ? navigationHandler(UserOnboardingStep.PROFILE, {
                          prefix: ClaimPrefix.ORG_USER_PROFILE,
                          urlParams: [
                            ...defaultUrlParams,
                            { name: 'userHash', value: claimHash },
                          ],
                        })
                      : navigationHandler(OrgOnboardingStep.REQUEST_APPROVAL)
                  }
                  handleGoNextStep={navigationHandler(UserOnboardingStep.TOS)}
                  {...props}
                />
              )}
            />

            <Route
              exact
              path={`${pathPrefix}/${UserOnboardingStep.TOS}`}
              render={props => (
                <ReviewTOSView
                  goBackStep={(isClaimed: boolean, isApprover: boolean) => {
                    if (isClaimed) {
                      return isApprover
                        ? null
                        : navigationHandler(OrgOnboardingStep.REQUEST_APPROVAL);
                    }
                    return navigationHandler(UserOnboardingStep.PASSWORD);
                  }}
                  {...props}
                />
              )}
            />
          </Switch>
        </FocusedCard>
      </UserProviderWrapper>
    </OrgOnboardingContextProvider>
  );
};
